/**
 * Суурь компонентууд — CLAUDE.md «Компонент» хүснэгт.
 *
 * Радиус: карт 12px · товч/таб/чип/шошго/input = 999px (pill)
 */

import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { ButtonHTMLAttributes, ComponentPropsWithRef, ReactNode } from 'react';
import {
  formatMnt, formatQty, maskIfHidden, TRANSFER_STATUS_MN, type TransferStatusKey,
} from '@boots/domain';
import type { ItemStatus } from '../lib/types';
import { STATUS_LABEL } from '../lib/types';
import { EmptyState } from './empty-state';

// ---------------------------------------------------------- Карт
// `surface` (index.css) — гэрэлд цагаан карт, харанхуйд шилэн gradient.
export function Card({ children, className = '' }: { children: ReactNode; className?: string }) {
  return (
    <div className={`surface rounded-card p-4 transition-colors sm:p-card ${className}`}>
      {children}
    </div>
  );
}

/** Хэсгийн шошго — «САГС», «ТӨЛБӨР». Өмнө нь гэрэлтдэг оюу цэг (dark glass). */
export function CardTitle({ children, right }: { children: ReactNode; right?: ReactNode }) {
  return (
    <div className="mb-3 flex items-center justify-between gap-3">
      <h2 className="flex items-center gap-2.5 text-eyebrow text-ink-2 uppercase">
        <span aria-hidden className="size-1.5 shrink-0 rounded-pill bg-accent shadow-[0_0_8px_var(--color-accent)]" />
        {children}
      </h2>
      {right}
    </div>
  );
}

// ---------------------------------------------------------- KPI
// ⚠️ Энд байсан `Kpi`-г УСТГАВ. KPI карт нь `components/stat-card.tsx`
//    дахь `StatCard` — өмнө нь энэ файлд нэг, `dashboard.tsx`-д нэг,
//    хуудас тус бүрд бас нэг гэсэн ЗУРГААН хувилбар зэрэг оршиж
//    байсан. Шинэ KPI хэрэгтэй бол `StatCard`-ыг дуудна.

// ---------------------------------------------------------- Товч
type BtnProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'primary' | 'ghost' | 'danger';
};

export function Button({ variant = 'primary', className = '', ...props }: BtnProps) {
  // Дарахад жижгэрээд буцна (dark glass: 1 → .97 → 1)
  const base =
    'inline-flex min-h-11 items-center justify-center gap-2 rounded-btn px-4 py-2 ' +
    'text-sm font-medium transition active:scale-[0.97] sm:min-h-0 ' +
    'disabled:cursor-not-allowed disabled:opacity-45';
  const tone = {
    // `fill-primary` — харанхуйд gradient + гэрэлтэлт (index.css)
    primary: 'fill-primary bg-accent text-white hover:bg-accent-fill-hover',
    ghost: 'border border-hair bg-panel text-ink hover:bg-accent-100',
    danger: 'bg-alert text-white hover:opacity-90',
  }[variant];
  return <button className={`${base} ${tone} ${className}`} {...props} />;
}

// ---------------------------------------------------------- Input
// React 19-д `ref` нь энгийн prop — forwardRef хэрэггүй.
export function Input({ className = '', ...props }: ComponentPropsWithRef<'input'>) {
  return (
    <input
      className={`w-full rounded-field border border-hair bg-input px-4 py-2 text-base
        text-ink transition placeholder:text-ink-3 focus:border-accent focus:ring-4
        focus:ring-accent-100 focus:outline-none sm:text-sm ${className}`}
      {...props}
    />
  );
}

export function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-xs font-medium text-ink-2">{label}</span>
      {children}
    </label>
  );
}

export function Select({
  value, onChange, options, placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
  placeholder?: string;
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full appearance-none rounded-field border border-hair bg-input px-4 py-2
        text-base text-ink transition focus:border-accent focus:ring-4 focus:ring-accent-100
        focus:outline-none sm:text-sm"
    >
      {placeholder ? <option value="">{placeholder}</option> : null}
      {options.map((o) => (
        <option key={o.value} value={o.value}>{o.label}</option>
      ))}
    </select>
  );
}

// ---------------------------------------------------------- Чип, шошго
export function Chip({
  active = false, children, onClick, className = '',
}: {
  active?: boolean;
  children: ReactNode;
  onClick?: () => void;
  /** Хэмжээ томруулах (касс: 34px өндөр). Өнгийг энд бүү дамжуул. */
  className?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      // `chip-on` — сонгоход «ойлгоно»; харанхуйд gradient (index.css)
      className={`inline-flex min-h-11 items-center rounded-chip border px-[11px] py-2 text-xs
        transition active:scale-[0.97] sm:min-h-0 sm:py-1 ${
        active ? 'chip-on border-transparent' : 'border-hair bg-input text-ink-2 hover:bg-accent-100/50'
      } ${className}`}
    >
      {children}
    </button>
  );
}

const STATUS_TONE: Record<ItemStatus, string> = {
  COLLECTED: 'bg-hair/60 text-ink-2',
  AVAILABLE: 'bg-accent-100 text-accent-700',
  RESERVED: 'bg-warn-bg text-warn-ink',
  SOLD: 'bg-hair/60 text-ink-2',
  RETURNED: 'bg-warn-bg text-warn-ink',
  DEFECTIVE: 'bg-alert/10 text-alert-ink',
  WRITTEN_OFF: 'bg-alert/10 text-alert-ink',
};

export function StatusBadge({ status }: { status: ItemStatus }) {
  return (
    <span className={`inline-block rounded-full px-[11px] py-1 text-xs whitespace-nowrap ${STATUS_TONE[status]}`}>
      {STATUS_LABEL[status]}
    </span>
  );
}

const TRANSFER_TONE: Record<TransferStatusKey, string> = {
  DRAFT: 'bg-hair/60 text-ink-2',
  IN_TRANSIT: 'bg-warn-bg text-warn-ink',
  RECEIVED: 'bg-accent-100 text-accent-700',
  PARTIAL: 'bg-alert-bg text-alert-ink',
};

/** Шилжүүлгийн төлөв — «Шилжүүлэг» ба «Хүлээн авалт» хуудас хуваалцана. */
export function TransferStatusBadge({ status }: { status: TransferStatusKey }) {
  return (
    <span className={`inline-block rounded-full px-2.5 py-[3px] text-[11px] whitespace-nowrap ${TRANSFER_TONE[status]}`}>
      {TRANSFER_STATUS_MN[status]}
    </span>
  );
}

// ---------------------------------------------------------- Мөнгө, тоо
/** ⚠️ Дэлгэцэд `toLocaleString()` шууд дуудахгүй — @boots/domain-аас. */
export function Money({ value, show = true }: { value: unknown; show?: boolean }) {
  return <span className="num whitespace-nowrap">{maskIfHidden(formatMnt(value), show)}</span>;
}

// ---------------------------------------------------------- Хүснэгт
export function Table({ children }: { children: ReactNode }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse text-sm">{children}</table>
    </div>
  );
}

export function Th({ children, num = false }: { children: ReactNode; num?: boolean }) {
  return (
    <th
      {...(num ? { 'data-num': 'true' } : {})}
      className={`border-b border-hair px-3 py-2 text-[11.5px] font-medium tracking-wide
        text-ink-2 uppercase ${num ? 'text-right' : 'text-left'}`}
    >
      {children}
    </th>
  );
}

export function Td({ children, num = false, className = '' }: { children: ReactNode; num?: boolean; className?: string }) {
  return (
    <td
      {...(num ? { 'data-num': 'true' } : {})}
      className={`border-b border-hair/70 px-3 py-2.5 ${num ? 'text-right whitespace-nowrap' : ''} ${className}`}
    >
      {children}
    </td>
  );
}

export function Tr({ children, onClick }: { children: ReactNode; onClick?: () => void }) {
  return (
    <tr
      onClick={onClick}
      className={onClick ? 'cursor-pointer transition hover:bg-row-hover' : undefined}
    >
      {children}
    </tr>
  );
}

// ---------------------------------------------------------- Төлөв
/**
 * Нэг мөрийн хоосон төлөв.
 *
 * ⚠️ Дараагийн алхмын ТОВЧТОЙ хоосон төлөв хэрэгтэй бол
 *    `components/empty-state.tsx` дахь `EmptyState`-ыг дуудна.
 *    Энэ нь текст л дамжуулдаг хуучин дуудлагуудыг ажиллуулж
 *    байгаа тул тэмдэг, зайг тэдэнтэй нийцүүлэв.
 */
export function Empty({ children }: { children: ReactNode }) {
  if (typeof children === 'string') return <EmptyState compact title={children} />;
  return <div className="px-3 py-10 text-center text-sm text-ink-2">{children}</div>;
}

export function Spinner({ label = 'Ачаалж байна…' }: { label?: string }) {
  return (
    <div
      className="flex items-center gap-3 px-3 py-10 text-sm text-ink-2"
      role="status"
      aria-live="polite"
    >
      <span
        aria-hidden
        className="h-4 w-4 animate-spin rounded-full border-2 border-hair border-t-accent"
      />
      {label}
    </div>
  );
}

export function ErrorNote({ children }: { children: ReactNode }) {
  return (
    <div className="rounded-card border border-alert/30 bg-alert/5 px-4 py-3 text-sm text-alert-ink">
      {children}
    </div>
  );
}

export function OkNote({ children }: { children: ReactNode }) {
  return (
    <div className="rounded-card border border-accent/25 bg-accent-100 px-4 py-3 text-sm text-accent-700">
      {children}
    </div>
  );
}

// ---------------------------------------------------------- Талбарууд
export function Textarea({ className = '', ...props }: ComponentPropsWithRef<'textarea'>) {
  return (
    <textarea
      className={`w-full rounded-card border border-hair bg-input px-4 py-2.5 text-base
        text-ink transition placeholder:text-ink-3 focus:border-accent focus:ring-4
        focus:ring-accent-100 focus:outline-none sm:text-sm ${className}`}
      {...props}
    />
  );
}

// ---------------------------------------------------------- Модал
/**
 * Төвлөрсөн диалог. Escape болон дэвсгэр дарахад хаагдана.
 *
 * ⚠️ Бичих үйлдэл бүр баталгаажуулалт шаарддаг тул нэг л загвар —
 * хуудас бүр өөрийн модал зохиохгүй.
 */
export function Modal({
  title, subtitle, onClose, children, footer, wide = false,
}: {
  title: string;
  subtitle?: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  wide?: boolean;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center overflow-y-auto bg-scrim/45
        sm:items-start sm:p-8"
    >
      {/* Дэвсгэр — дарахад хаагдана */}
      <button type="button" aria-label="Хаах" onClick={onClose} className="fixed inset-0 cursor-default" />
      {/*
        Гар утсанд ЁРООЛООС гарах хуудас (bottom sheet): эрхий хуруунд
        ойр, дээд талаас нь дэвсгэр харагдана. `sm:`-ээс дээш нь өмнөх
        төвлөрсөн диалог хэвээр — my-auto, бүтэн радиус, гадна зай.
      */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className={`relative max-h-[92dvh] w-full overflow-y-auto rounded-t-pop border border-hair
          bg-panel shadow-pop sm:my-auto sm:max-h-none sm:overflow-visible sm:rounded-pop
          ${wide ? 'max-w-[880px]' : 'max-w-[460px]'}`}
      >
        <div className="flex items-start gap-3 border-b border-hair px-card py-3.5">
          <div className="min-w-0 flex-1">
            <h2 className="text-[15px] font-semibold">{title}</h2>
            {subtitle ? <p className="mt-0.5 text-[12.5px] text-ink-2">{subtitle}</p> : null}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Хаах"
            className="-mt-1 -mr-1 grid size-11 place-items-center rounded-full text-ink-2
              transition hover:bg-well sm:size-auto sm:px-2 sm:py-1"
          >
            ✕
          </button>
        </div>
        <div className="flex flex-col gap-3 px-card py-4">{children}</div>
        {footer ? (
          <div className="flex flex-wrap items-center justify-end gap-2 border-t border-hair px-card py-3.5">
            {footer}
          </div>
        ) : null}
      </div>
    </div>
  );
}

// ---------------------------------------------------------- Таб
/**
 * Таб бүлэг — идэвхтэй pill нь сонголтын хооронд ГУЛСАНА (dark glass, 0.5s).
 *
 * ⚠️ Заагчийн байрлалыг DOM-оос хэмжинэ (`offsetLeft/Width`): кирилл
 *    шошгоны өргөн фонт ачаалагдахад өөрчлөгддөг тул `ResizeObserver`.
 * ⚠️ Хэмжилтийн хамаарал нь `tabs`-ын ТҮЛХҮҮР мөр, массив биш —
 *    дуудагчид массивыг render бүрд шинээр үүсгэдэг тул массиваар
 *    хамааруулбал effect → setState → render давталт үүснэ.
 */
export function Tabs<T extends string>({
  tabs, value, onChange, scroll = false,
}: {
  tabs: { key: T; label: string; count?: number }[];
  value: T;
  onChange: (v: T) => void;
  /**
   * Нарийн дэлгэцэд олон мөр болж боогдохын оронд НЭГ мөрөөр хажуу тийш
   * гүйлгэнэ — боогдсон pill зам том зууван болж, доорх агуулгыг түлхдэг.
   */
  scroll?: boolean;
}) {
  const trackRef = useRef<HTMLDivElement>(null);
  const [pill, setPill] = useState<{ x: number; y: number; w: number; h: number } | null>(null);
  const shape = tabs.map((t) => `${t.key}:${t.label}:${t.count ?? ''}`).join('|');

  useLayoutEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    const measure = () => {
      const el = track.querySelector<HTMLElement>('[data-active="true"]');
      setPill((prev) => {
        if (!el) return null;
        const next = { x: el.offsetLeft, y: el.offsetTop, w: el.offsetWidth, h: el.offsetHeight };
        return prev && prev.x === next.x && prev.y === next.y && prev.w === next.w && prev.h === next.h
          ? prev
          : next;
      });
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(track);
    return () => ro.disconnect();
  }, [value, shape]);

  const track = (
    <div
      ref={trackRef}
      className={`relative flex gap-1 rounded-full bg-shell p-[3px] ${scroll ? 'w-max' : 'flex-wrap'}`}
    >
      {pill ? (
        <span
          aria-hidden
          style={{ transform: `translate(${pill.x}px, ${pill.y}px)`, width: pill.w, height: pill.h }}
          className="fill-primary absolute top-0 left-0 rounded-full bg-accent-fill
            transition-[transform,width] duration-500 ease-[cubic-bezier(0.65,0,0.35,1)]"
        />
      ) : null}
      {tabs.map((t) => (
        <button
          key={t.key}
          type="button"
          data-active={value === t.key}
          onClick={() => onChange(t.key)}
          className={`relative inline-flex min-h-11 items-center rounded-full px-3.5 py-1.5
            text-[12.5px] transition-colors sm:min-h-0 ${
            value === t.key
              ? `font-semibold text-white ${pill ? '' : 'bg-accent-fill'}`
              : 'text-ink-2 hover:text-ink'
          }`}
        >
          {t.label}
          {t.count === undefined ? null : (
            // «Салбар 1» + 20 → «Салбар 1 · 20» — «Салбар 120» гэж уншигдахгүй
            <span className="num ml-1 opacity-70">{/\d$/.test(t.label) ? '· ' : ''}{formatQty(t.count)}</span>
          )}
        </button>
      ))}
    </div>
  );
  return scroll ? <div className="max-w-full overflow-x-auto">{track}</div> : track;
}

/** Хуудасны толгойн үйлдлийн мөр. */
export function PageActions({ children }: { children: ReactNode }) {
  return <div className="flex flex-wrap items-center gap-2">{children}</div>;
}

// ---------------------------------------------------------- Хуудаслалт
/**
 * Cursor хуудаслалтын мөр.
 *
 * Сервер нь `meta.nextCursor` л өгдөг тул БУЦАХ товч нь өмнөх
 * cursor-уудыг өөрөө хураана — дуудагч тал `stack` хадгална.
 */
export function Pager({ page, canPrev, canNext, onPrev, onNext }: {
  page: number;
  canPrev: boolean;
  canNext: boolean;
  onPrev: () => void;
  onNext: () => void;
}) {
  const btn =
    'inline-flex min-h-11 min-w-11 items-center justify-center rounded-full px-2 py-1.5 ' +
    'text-[12.5px] text-ink-2 transition hover:bg-well sm:min-h-0 sm:min-w-[30px] ' +
    'disabled:cursor-not-allowed disabled:opacity-35';

  return (
    <div className="flex items-center gap-3 border-t border-hair pt-3.5">
      <span className="num text-[12.5px] text-ink-2">Хуудас {page}</span>
      <div className="ml-auto flex gap-1.5">
        <button type="button" className={btn} disabled={!canPrev} onClick={onPrev} aria-label="Өмнөх хуудас">‹</button>
        <button type="button" className={btn} disabled={!canNext} onClick={onNext} aria-label="Дараагийн хуудас">›</button>
      </div>
    </div>
  );
}
